import { Component, OnInit, TemplateRef, ViewChild, AfterViewInit, ChangeDetectorRef, Input, Output, EventEmitter, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { HeaderComponent } from '../../header/header.component';
import { PageHeaderComponent } from '../../page-header/page-header.component';
import { ProviderSidebarComponent } from '../../../../provider/sidebar/provider-sidebar.component';
import { OrgAdminSidebarComponent } from '../../../../org-admin/sidebar/org-admin-sidebar.component';
import { StepperComponent, StepperStep } from '../../primeng';
import { YearRangePickerComponent } from '../../primeng/year-range-picker/year-range-picker.component';
import { CatalogueApiService, AuthApiService } from '@angular-workspace/services';
import { ToasterService } from '../../../../services/toaster.service';
import { FileFormatConfigService } from '../../../services/file-format-config.service';

export interface DatasetData {
  title: string;
  permission: 'Open' | 'Restricted' | 'Private';
  metadataOption: 'enter' | 'upload' | null;
}

@Component({
  selector: 'app-provider-preview-metadata',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    FormsModule,
    HeaderComponent,
    ProviderSidebarComponent,
    OrgAdminSidebarComponent,
    PageHeaderComponent,
    StepperComponent,
    YearRangePickerComponent,
  ],
  templateUrl: './preview-metadata.component.html',
  styleUrl: './preview-metadata.component.scss',
})
export class ProviderPreviewMetadataComponent implements OnInit, AfterViewInit {

  get isOrgAdminRoute(): boolean {
    return this.router.url.includes('/org-admin/');
  }

  currentStep = 2; // Step 3 (0-based index) - Preview Metadata
  totalSteps = 4;

  @ViewChild('step3Template', { static: false }) step3Template!: TemplateRef<any>;

  // Inputs for editable mode
  @Input() editableMode: boolean = false;
  @Input() assetData: any = null;
  @Input() assetType: 'dataset' | 'model' | null = null;
  /** When true, Save Changes is enabled even when form is not dirty (e.g. modify-asset modal). */
  @Input() allowSaveWithoutDirty: boolean = false;

  // Outputs for form submission
  @Output() formSubmit = new EventEmitter<any>();
  @Output() formCancel = new EventEmitter<void>();

  stepperSteps: StepperStep[] = [
    {
      key: 'step1',
      label: 'Setup your datasets',
    },
    {
      key: 'step2',
      label: 'Upload metadata',
    },
    {
      key: 'step3',
      label: 'Preview Metadata',
    },
    {
      key: 'step4',
      label: 'Upload files',
    },
  ];

  /** Step 1 label from route: "Setup your Models" on add-model, "Setup your datasets" on add-dataset */
  get stepperStepsWithLabel(): StepperStep[] {
    const label = this.router.url.includes('add-model') ? 'Setup your Models' : 'Setup your datasets';
    return this.stepperSteps.map((step, i) => i === 0 ? { ...step, label } : step);
  }

  datasetData: DatasetData | null = null;
  metadataForm!: FormGroup;
  additionalInfoForm!: FormGroup;

  // Metadata fields
  parsedMetadata: any = {};

  // Additional info fields
  parsedAdditionalInfo: any = {};

  // Cached data descriptors for template access
  dataDescriptorsList: any[] = [];

  // Flag to prevent duplicate API calls
  isCreatingItem = false;

  // Tag input for editable mode
  tagInput: string = '';

  // Dropdown open state (editable mode)
  isPermissionDropdownOpen = false;
  isResourceServerDropdownOpen = false;
  isUploadFrequencyDropdownOpen = false;
  isLicenseDropdownOpen = false;
  isDatasetTypeDropdownOpen = false;
  isFileFormatDropdownOpen = false;

  // Options for dropdowns (same as in metadata and additional-info components)
  licenseOptions: Array<{ value: string; label: string }> = [
    { value: 'Open Government License, India', label: 'Open Government License, India' },
    { value: 'CC-BY 4.0', label: 'CC-BY 4.0' },
    { value: 'Other', label: 'Other' }
  ];

  industryOptions: Array<{ value: string; label: string }> = [
    { value: 'Agriculture', label: 'Agriculture' },
    { value: 'forest', label: 'Forest' },
    { value: 'Water', label: 'Water' },
    { value: 'Energy', label: 'Energy' },
    { value: 'Transport', label: 'Transport' },
    { value: 'Health', label: 'Health' },
    { value: 'Education', label: 'Education' },
    { value: 'Other', label: 'Other' }
  ];

  resourceServerOptions: Array<{ value: string; label: string; displayLabel: string; name: string; url: string }> = [];

  uploadFrequencyOptions: Array<{ value: string; label: string }> = [
    { value: 'OneTime', label: 'OneTime' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'Quarterly', label: 'Quarterly' },
    { value: 'semi-annually', label: 'Semi-Annually' },
    { value: 'Annually', label: 'Annually' }
  ];

  datasetTypeOptions: Array<{ value: string; label: string }> = [
    { value: 'Structured', label: 'Structured' },
    { value: 'semi Structured', label: 'semi Structured' },
    { value: 'unStructured', label: 'unStructured' }
  ];

  fileFormatOptions: Array<{ value: string; label: string }> = [];

  private fileFormatMap: { [key: string]: Array<{ value: string; label: string }> } = {};
  private modelFileFormats: Array<{ value: string; label: string }> = [];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private catalogueApiService: CatalogueApiService,
    private authApiService: AuthApiService,
    private toasterService: ToasterService,
    private fileFormatConfigService: FileFormatConfigService
  ) { }

  /**
   * Get the localStorage key based on current route
   */
  private getLocalStorageKey(): string {
    const currentUrl = this.router.url;
    if (currentUrl.includes('/add-model/')) {
      return 'modelFormData';
    }
    return 'datasetFormData'; // Default to dataset
  }

  async ngOnInit(): Promise<void> {
    this.fileFormatMap = await this.fileFormatConfigService.loadDatasetFileFormatMap();
    this.modelFileFormats = await this.fileFormatConfigService.getModelFileFormats();

    // If editable mode with assetData provided, skip localStorage
    if (this.editableMode && this.assetData) {
      this.initializeResourceServerOptions();
      this.populateFromAssetData(this.assetData);
      this.initializeForms();
      this.cdr.detectChanges();
      return;
    }

    const localStorageKey = this.getLocalStorageKey();

    // Get dataset/model data from localStorage
    const savedData = localStorage.getItem(localStorageKey);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        this.datasetData = {
          title: parsed.title || '',
          permission: parsed.permission || 'Open',
          metadataOption: parsed.metadataOption || null
        };

        // Parse JSON schema if it exists (from upload-metadata step)
        if (parsed.jsonSchema) {
          try {
            const jsonData = typeof parsed.jsonSchema === 'string'
              ? JSON.parse(parsed.jsonSchema)
              : parsed.jsonSchema;

            this.parsedMetadata = {
              shortDescription: jsonData.shortDescription || '',
              longDescription: jsonData.longDescription || jsonData.description || '',
              license: jsonData.license || '',
              otherLicense: jsonData.otherLicense || '',
              resourceServer: jsonData.resourceServer || '',
              uploadFrequency: jsonData.uploadFrequency || '',
              yearRange: jsonData.yearRange || null,
              geoCoverage: jsonData.geoCoverage || '',
              industry: jsonData.industry || ''
            };

            // Parse dataDescriptors from file (array or object map), normalize to { attributeName, parameterType, schemaType }
            const dataDescriptors = this.parseDataDescriptorsFromJson(jsonData);

            this.parsedAdditionalInfo = {
              datasetType: jsonData.datasetType || '',
              fileFormat: jsonData.fileFormat || '',
              tags: Array.isArray(jsonData.tags) ? jsonData.tags : [],
              sampleDataUrl: jsonData.sampleDataUrl || '',
              dataDescriptors: dataDescriptors
            };

            // Cache the filtered descriptors list for template
            this.dataDescriptorsList = dataDescriptors;
          } catch (error) {
            console.error('Error parsing JSON schema:', error);
          }
        } else {
          // Extract metadata from direct formData (Enter Metadata flow)
          this.parsedMetadata = {
            shortDescription: parsed.shortDescription || '',
            longDescription: parsed.longDescription || '',
            license: parsed.license || '',
            otherLicense: parsed.otherLicense || '',
            resourceServer: parsed.resourceServer || '',
            uploadFrequency: parsed.uploadFrequency || '',
            yearRange: parsed.yearRange || null,
            geoCoverage: parsed.geoCoverage || '',
            industry: parsed.industry || ''
          };

          // Parse dataDescriptors from formData (supports array; normalize keys)
          const dataDescriptors = this.parseDataDescriptorsFromJson(parsed);

          this.parsedAdditionalInfo = {
            datasetType: parsed.datasetType || '',
            fileFormat: parsed.fileFormat || '',
            tags: Array.isArray(parsed.tags) ? parsed.tags : [],
            sampleDataUrl: parsed.sampleDataUrl || '',
            dataDescriptors: dataDescriptors
          };

          // Cache the filtered descriptors list for template
          this.dataDescriptorsList = dataDescriptors;
        }

        // Initialize resource server options
        this.initializeResourceServerOptions();

        // Initialize forms with parsed data
        this.initializeForms();
      } catch (error) {
        console.error('Error parsing saved data:', error);
        localStorage.removeItem(localStorageKey);
      }
    } else {
      // Initialize empty forms if no data
      this.dataDescriptorsList = [];
      this.initializeForms();
    }

    // Ensure dataDescriptorsList is always initialized
    if (!this.dataDescriptorsList) {
      this.dataDescriptorsList = [];
    }
  }

  /**
   * Normalize a descriptor object to { attributeName, parameterType, schemaType }.
   * Accepts both camelCase and snake_case from file/API.
   */
  private normalizeDataDescriptor(desc: any): { attributeName: string; parameterType: string; schemaType: string } | null {
    if (!desc || typeof desc !== 'object') return null;
    const attributeName = desc.attributeName ?? desc.attribute_name ?? '';
    const parameterType = desc.parameterType ?? desc.parameter_type ?? '';
    const schemaType = desc.schemaType ?? desc.schema_type ?? '';
    if (!attributeName || !parameterType || !schemaType) return null;
    return { attributeName: String(attributeName).trim(), parameterType: String(parameterType).trim(), schemaType: String(schemaType).trim() };
  }

  /**
   * Build dataDescriptors array from JSON: supports dataDescriptors (array) or dataDescriptor (object map).
   */
  private parseDataDescriptorsFromJson(jsonData: any): any[] {
    const out: Array<{ attributeName: string; parameterType: string; schemaType: string }> = [];
    if (Array.isArray(jsonData.dataDescriptors)) {
      jsonData.dataDescriptors.forEach((desc: any) => {
        const normalized = this.normalizeDataDescriptor(desc);
        if (normalized) out.push(normalized);
      });
    }
    if (out.length === 0 && jsonData.dataDescriptor && typeof jsonData.dataDescriptor === 'object' && !Array.isArray(jsonData.dataDescriptor)) {
      Object.entries(jsonData.dataDescriptor).forEach(([key, val]: [string, any]) => {
        const obj = val && typeof val === 'object' ? { attributeName: key, parameterType: val.parameterType ?? val.parameter_type ?? '', schemaType: val.schemaType ?? val.schema_type ?? '' } : null;
        if (obj && obj.parameterType && obj.schemaType) out.push(obj);
      });
    }
    return out;
  }

  /**
   * Normalize resourceServer from API (object/array/string) to option value string
   */
  private normalizeResourceServerValue(raw: any): string {
    if (!raw) return '';
    const opts = this.resourceServerOptions || [];
    if (typeof raw === 'string') {
      const found = opts.find(opt => opt.value === raw || opt.url === raw);
      return found ? found.value : raw;
    }
    if (Array.isArray(raw) && raw.length > 0) {
      const first = raw[0];
      const url = typeof first === 'string' ? first : (first?.url ?? first?.value ?? '');
      const found = opts.find(opt => opt.value === url || opt.url === url || (first?.name && opt.name === first.name));
      return found ? found.value : (typeof first === 'string' ? first : url || '');
    }
    if (typeof raw === 'object') {
      const url = raw.url ?? raw.value ?? '';
      const found = opts.find(opt => opt.value === url || opt.url === url || (raw.name && opt.name === raw.name));
      return found ? found.value : (url || '');
    }
    return '';
  }

  /**
   * Initialize resource server options
   */
  private initializeResourceServerOptions(): void {
    // This should match the options from metadata component
    // For now, using default values
    this.resourceServerOptions = [
      { value: 'http://v2.dev.ogc.iudx.io/', name: 'OGC', url: 'v2.dev.ogc.iudx.io/', label: 'OGC (v2.dev.ogc.iudx.io/)', displayLabel: 'OGC (v2.dev.ogc.iudx.io/)' },
      { value: 'https://v2.dev.rs.iudx.io/', name: 'NGSI-LD', url: 'v2.dev.rs.iudx.io/', label: 'NGSI-LD (v2.dev.rs.iudx.io/)', displayLabel: 'NGSI-LD (v2.dev.rs.iudx.io/)' },
      { value: 'https://v2.dev.rs-proxy.iudx.io/', name: 'GATEWAY', url: 'v2.dev.rs-proxy.iudx.io/', label: 'GATEWAY (v2.dev.rs-proxy.iudx.io/)', displayLabel: 'GATEWAY (v2.dev.rs-proxy.iudx.io/)' }
    ];
  }

  /** Toggle permission dropdown and select options in modify (editable) mode. */
  togglePermissionDropdown(): void {
    if (!this.editableMode || !this.allowSaveWithoutDirty) return;
    this.isPermissionDropdownOpen = !this.isPermissionDropdownOpen;
  }

  selectPermissionOption(value: 'Open' | 'Restricted'): void {
    if (!this.editableMode || !this.allowSaveWithoutDirty || !this.datasetData) return;
    this.datasetData.permission = value;
    this.isPermissionDropdownOpen = false;
  }

  getSelectedPermissionLabelForEdit(): string {
    const p = this.datasetData?.permission;
    if (!p) return 'Select permission';
    return p === 'Open' ? 'Public' : p;
  }

  /**
   * Populate data from assetData input (for editable mode)
   */
  private populateFromAssetData(assetData: any): void {
    console.log('PREVIEW-METADATA: populateFromAssetData - assetData:', assetData);

    const isModel = this.assetType === 'model' ||
      (assetData.type && Array.isArray(assetData.type) && assetData.type.some((t: string) => t.includes('AiModel'))) ||
      (assetData.type && typeof assetData.type === 'string' && assetData.type.includes('AiModel'));

    console.log('PREVIEW-METADATA: populateFromAssetData - isModel:', isModel);

    // Map access policy
    const accessPolicy = assetData.accessPolicy || assetData.access_policy || 'OPEN';
    const permission = accessPolicy === 'OPEN' ? 'Open' :
      accessPolicy === 'RESTRICTED' ? 'Restricted' : 'Private';

    this.datasetData = {
      title: assetData.name || assetData.label || assetData.title || '',
      permission: permission,
      metadataOption: 'enter'
    };

    console.log('PREVIEW-METADATA: populateFromAssetData - datasetData:', this.datasetData);

    // Parse year range if it's a string
    let yearRange = assetData.yearRange || assetData.year_range || null;
    console.log('PREVIEW-METADATA: populateFromAssetData - yearRange (raw):', yearRange);

    if (typeof yearRange === 'string' && yearRange.includes('-')) {
      const parts = yearRange.split('-').map((p: string) => p.trim());
      if (parts.length === 2) {
        yearRange = {
          startYear: parseInt(parts[0]) || null,
          endYear: parseInt(parts[1]) || null
        };
      }
    }
    console.log('PREVIEW-METADATA: populateFromAssetData - yearRange (parsed):', yearRange);

    const rawResourceServer = assetData.resourceServer ?? assetData.resource_server ?? '';
    console.log('PREVIEW-METADATA: populateFromAssetData - rawResourceServer:', rawResourceServer);
    const resourceServerValue = this.normalizeResourceServerValue(rawResourceServer);
    console.log('PREVIEW-METADATA: populateFromAssetData - resourceServerValue (normalized):', resourceServerValue);

    this.parsedMetadata = {
      shortDescription: assetData.shortDescription || assetData.short_description || '',
      longDescription: assetData.description || assetData.longDescription || '',
      license: assetData.license || 'Open Government License, India',
      otherLicense: assetData.otherLicense || assetData.other_license || '',
      resourceServer: resourceServerValue,
      uploadFrequency: assetData.uploadFrequency || assetData.upload_frequency || 'OneTime',
      yearRange: yearRange,
      geoCoverage: assetData.geoCoverage || assetData.geo_coverage || '',
      industry: assetData.industry || 'Agriculture'
    };

    console.log('PREVIEW-METADATA: populateFromAssetData - parsedMetadata:', this.parsedMetadata);

    this.parsedAdditionalInfo = {
      datasetType: isModel ? null : (assetData.dataBankType || assetData.datasetType || 'Structured'),
      fileFormat: assetData.fileFormat || assetData.file_format || 'csv',
      tags: Array.isArray(assetData.tags) ? assetData.tags : (assetData.tags ? [assetData.tags] : []),
      sampleDataUrl: assetData.mediaURL || assetData.media_url || assetData.sampleDataUrl || '',
      dataDescriptors: assetData.dataDescriptor || assetData.data_descriptor || []
    };

    console.log('PREVIEW-METADATA: populateFromAssetData - parsedAdditionalInfo:', this.parsedAdditionalInfo);

    // Cache descriptors list
    this.dataDescriptorsList = Array.isArray(this.parsedAdditionalInfo.dataDescriptors)
      ? this.parsedAdditionalInfo.dataDescriptors
      : [];
  }

  /**
   * Initialize form groups with parsed data
   */
  private initializeForms(): void {
    console.log('PREVIEW-METADATA: initializeForms - START');
    console.log('PREVIEW-METADATA: initializeForms - editableMode:', this.editableMode);
    console.log('PREVIEW-METADATA: initializeForms - assetType:', this.assetType);
    console.log('PREVIEW-METADATA: initializeForms - isModel():', this.isModel());
    console.log('PREVIEW-METADATA: initializeForms - parsedMetadata:', this.parsedMetadata);
    console.log('PREVIEW-METADATA: initializeForms - parsedAdditionalInfo:', this.parsedAdditionalInfo);

    const isDisabled = !this.editableMode;
    const isModelAsset = this.isModel();

    // Base validators for common fields
    const validators = this.editableMode ? [Validators.required] : [];
    const shortDescValidators = this.editableMode ? [Validators.required, Validators.minLength(50), Validators.maxLength(200)] : [];
    const longDescValidators = this.editableMode ? [Validators.required, Validators.minLength(50), Validators.maxLength(2000)] : [];

    // Dataset-only validators (not required for models)
    const datasetOnlyValidators = this.editableMode && !isModelAsset ? [Validators.required] : [];

    console.log('PREVIEW-METADATA: initializeForms - isModelAsset:', isModelAsset);
    console.log('PREVIEW-METADATA: initializeForms - datasetOnlyValidators:', datasetOnlyValidators);

    // Initialize metadata form
    this.metadataForm = this.fb.group({
      shortDescription: [{ value: this.parsedMetadata.shortDescription || '', disabled: isDisabled }, shortDescValidators],
      longDescription: [{ value: this.parsedMetadata.longDescription || '', disabled: isDisabled }, longDescValidators],
      license: [{ value: this.parsedMetadata.license || '', disabled: isDisabled }, validators],
      otherLicense: [{ value: this.parsedMetadata.otherLicense || '', disabled: isDisabled }],
      resourceServer: [{ value: this.parsedMetadata.resourceServer || '', disabled: isDisabled }, datasetOnlyValidators],
      uploadFrequency: [{ value: this.parsedMetadata.uploadFrequency || '', disabled: isDisabled }, validators],
      yearRange: [{ value: this.parsedMetadata.yearRange || null, disabled: isDisabled }, datasetOnlyValidators],
      geoCoverage: [{ value: this.parsedMetadata.geoCoverage || '', disabled: isDisabled }, datasetOnlyValidators],
      industry: [{ value: this.parsedMetadata.industry || '', disabled: isDisabled }, validators]
    });

    console.log('PREVIEW-METADATA: initializeForms - metadataForm created');
    console.log('PREVIEW-METADATA: initializeForms - metadataForm.value:', this.metadataForm.value);
    console.log('PREVIEW-METADATA: initializeForms - metadataForm.valid:', this.metadataForm.valid);
    console.log('PREVIEW-METADATA: initializeForms - metadataForm.errors:', this.metadataForm.errors);

    // Initialize additional info form
    this.additionalInfoForm = this.fb.group({
      datasetType: [{ value: this.parsedAdditionalInfo.datasetType || '', disabled: isDisabled }, datasetOnlyValidators],
      fileFormat: [{ value: this.parsedAdditionalInfo.fileFormat || '', disabled: isDisabled }, validators],
      tags: [{ value: this.parsedAdditionalInfo.tags || [], disabled: isDisabled }, validators],
      sampleDataUrl: [{ value: this.parsedAdditionalInfo.sampleDataUrl || '', disabled: isDisabled }],
      dataDescriptors: [{ value: this.parsedAdditionalInfo.dataDescriptors || [], disabled: isDisabled }]
    });

    console.log('PREVIEW-METADATA: initializeForms - additionalInfoForm created');
    console.log('PREVIEW-METADATA: initializeForms - additionalInfoForm.value:', this.additionalInfoForm.value);
    console.log('PREVIEW-METADATA: initializeForms - additionalInfoForm.valid:', this.additionalInfoForm.valid);
    console.log('PREVIEW-METADATA: initializeForms - additionalInfoForm.errors:', this.additionalInfoForm.errors);

    // Update cached descriptors list from form (use getRawValue so disabled controls are included).
    // If form returns a non-empty list, use it; otherwise keep existing list (e.g. from file upload).
    const fromForm = this.getDataDescriptors();
    if (fromForm.length > 0) {
      this.dataDescriptorsList = fromForm;
    }
    // else keep this.dataDescriptorsList as already set from parsed jsonSchema/formData

    // Update file format options based on dataset type
    if (this.parsedAdditionalInfo.datasetType) {
      this.updateFileFormatOptions(this.parsedAdditionalInfo.datasetType);
    }

    console.log('PREVIEW-METADATA: initializeForms - END');
  }

  /**
   * Update file format options based on dataset type (or model formats when isModel).
   */
  private updateFileFormatOptions(datasetType: string): void {
    this.fileFormatOptions = this.isModel()
      ? this.modelFileFormats
      : (this.fileFormatMap[datasetType] || []);
  }

  /** Whether any form has been modified (for Save Changes button in editable mode). */
  get isFormDirty(): boolean {
    return !!(this.metadataForm?.dirty || this.additionalInfoForm?.dirty);
  }

  /**
   * Check if current flow is for AI Model
   */
  isModel(): boolean {
    if (this.assetType) {
      return this.assetType === 'model';
    }
    return this.router.url.includes('/add-model/');
  }

  /**
   * Get whether Save Changes button should be disabled
   */
  get isSaveButtonDisabled(): boolean {
    const disabled = !this.allowSaveWithoutDirty && !this.isFormDirty;
    console.log('PREVIEW-METADATA: isSaveButtonDisabled:', disabled, {
      allowSaveWithoutDirty: this.allowSaveWithoutDirty,
      isFormDirty: this.isFormDirty,
      metadataFormDirty: this.metadataForm?.dirty,
      additionalInfoFormDirty: this.additionalInfoForm?.dirty
    });
    return disabled;
  }

  /**
   * Handle Save Changes button click (wrapper with logging)
   */
  onSaveChangesClick(): void {
    console.log('=== PREVIEW-METADATA: Save Changes button CLICKED ===');
    console.log('PREVIEW-METADATA: Button click event detected');
    console.log('PREVIEW-METADATA: Button disabled state:', this.isSaveButtonDisabled);
    this.onSubmit();
  }

  /**
   * Handle form submit (for editable mode)
   */
  onSubmit(): void {
    console.log('=== PREVIEW-METADATA: onSubmit CALLED ===');
    console.log('PREVIEW-METADATA: editableMode:', this.editableMode);
    console.log('PREVIEW-METADATA: allowSaveWithoutDirty:', this.allowSaveWithoutDirty);
    console.log('PREVIEW-METADATA: metadataForm exists:', !!this.metadataForm);
    console.log('PREVIEW-METADATA: additionalInfoForm exists:', !!this.additionalInfoForm);

    if (!this.editableMode) {
      console.warn('PREVIEW-METADATA: Not in editable mode - returning');
      return;
    }
    if (!this.metadataForm || !this.additionalInfoForm) {
      console.warn('PREVIEW-METADATA: Forms not initialized - returning');
      return;
    }

    console.log('PREVIEW-METADATA: metadataForm.valid:', this.metadataForm.valid);
    console.log('PREVIEW-METADATA: metadataForm.invalid:', this.metadataForm.invalid);
    console.log('PREVIEW-METADATA: metadataForm.errors:', this.metadataForm.errors);
    console.log('PREVIEW-METADATA: additionalInfoForm.valid:', this.additionalInfoForm.valid);
    console.log('PREVIEW-METADATA: additionalInfoForm.invalid:', this.additionalInfoForm.invalid);
    console.log('PREVIEW-METADATA: additionalInfoForm.errors:', this.additionalInfoForm.errors);

    // For modify mode (allowSaveWithoutDirty = true), allow submission even with validation errors
    // This is needed for existing assets that might have data not meeting current validation rules
    if (this.metadataForm.invalid || this.additionalInfoForm.invalid) {
      console.warn('PREVIEW-METADATA: Forms have validation errors');
      // Mark all fields as touched to show validation errors
      Object.keys(this.metadataForm.controls).forEach(key => {
        const control = this.metadataForm.get(key);
        control?.markAsTouched();
        if (control?.invalid) {
          console.log(`PREVIEW-METADATA: metadataForm.${key} is invalid:`, control.errors);
        }
      });
      Object.keys(this.additionalInfoForm.controls).forEach(key => {
        const control = this.additionalInfoForm.get(key);
        control?.markAsTouched();
        if (control?.invalid) {
          console.log(`PREVIEW-METADATA: additionalInfoForm.${key} is invalid:`, control.errors);
        }
      });

      // If not in allowSaveWithoutDirty mode, stop here (strict validation for new contributions)
      if (!this.allowSaveWithoutDirty) {
        console.warn('PREVIEW-METADATA: Stopping submission due to validation errors (strict mode)');
        return;
      }
      console.log('PREVIEW-METADATA: Allowing submission despite validation errors (modify mode with allowSaveWithoutDirty=true)');
    }

    // Combine form values
    const formData = {
      ...this.metadataForm.value,
      ...this.additionalInfoForm.value,
      title: this.datasetData?.title || '',
      permission: this.datasetData?.permission || 'Open'
    };

    console.log('PREVIEW-METADATA: Emitting formSubmit with formData:', formData);
    this.formSubmit.emit(formData);
    console.log('=== PREVIEW-METADATA: formSubmit emitted ===');
  }

  /**
   * Handle form cancel (for editable mode)
   */
  onCancel(): void {
    if (!this.editableMode) {
      return;
    }
    this.formCancel.emit();
  }

  /**
   * Add tag (for editable mode)
   */
  addTag(): void {
    if (!this.editableMode) {
      return;
    }
    const tag = this.tagInput.trim();
    if (tag && tag.length > 0) {
      const currentTags = this.getTags();
      // Check if tag already exists (case-insensitive)
      if (!currentTags.some(t => t.toLowerCase() === tag.toLowerCase())) {
        currentTags.push(tag);
        this.additionalInfoForm.patchValue({ tags: currentTags });
        this.additionalInfoForm.get('tags')?.markAsTouched();
        this.additionalInfoForm.markAsDirty();
        this.tagInput = '';
      }
    }
  }

  /**
   * Remove tag (for editable mode)
   */
  removeTag(tagToRemove: string): void {
    if (!this.editableMode) {
      return;
    }
    const currentTags = this.getTags();
    const updatedTags = currentTags.filter(tag => tag !== tagToRemove);
    this.additionalInfoForm.patchValue({ tags: updatedTags });
    this.additionalInfoForm.get('tags')?.markAsTouched();
    this.additionalInfoForm.markAsDirty();
  }

  /**
   * Handle tag input keydown (for editable mode)
   */
  onTagInputKeydown(event: KeyboardEvent): void {
    if (!this.editableMode) {
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addTag();
    }
  }

  /**
   * Get label for license
   */
  getLicenseLabel(value: string): string {
    const option = this.licenseOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  }

  /**
   * Get label for industry
   */
  getIndustryLabel(value: string): string {
    const option = this.industryOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  }

  /**
   * Get label for upload frequency
   */
  getUploadFrequencyLabel(value: string): string {
    const option = this.uploadFrequencyOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  }

  /**
   * Get label for dataset type
   */
  getDatasetTypeLabel(value: string): string {
    const option = this.datasetTypeOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  }

  /**
   * Get label for file format
   */
  getFileFormatLabel(value: string): string {
    const option = this.fileFormatOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  }

  /**
   * Get resource server display
   */
  getResourceServerDisplay(value: string): string {
    const option = this.resourceServerOptions.find(opt => opt.value === value);
    return option ? option.displayLabel : (typeof value === 'string' ? value : '');
  }

  // --- Editable mode dropdowns: Resource Server ---
  toggleResourceServerDropdown(): void {
    if (!this.editableMode) return;
    this.isResourceServerDropdownOpen = !this.isResourceServerDropdownOpen;
    if (this.isResourceServerDropdownOpen) {
      this.isUploadFrequencyDropdownOpen = false;
      this.isLicenseDropdownOpen = false;
      this.isDatasetTypeDropdownOpen = false;
      this.isFileFormatDropdownOpen = false;
    }
  }

  selectResourceServer(option: { value: string; label: string; displayLabel: string; name: string; url: string }): void {
    this.metadataForm.patchValue({ resourceServer: option.value });
    this.metadataForm.get('resourceServer')?.markAsTouched();
    this.metadataForm.markAsDirty();
    this.isResourceServerDropdownOpen = false;
  }

  getSelectedResourceServerLabel(): string {
    const v = this.metadataForm.get('resourceServer')?.value;
    return this.getResourceServerDisplay(v) || 'Select resource server';
  }

  isResourceServerSelected(value: string): boolean {
    return this.metadataForm.get('resourceServer')?.value === value;
  }

  // --- Editable mode dropdowns: Upload Frequency ---
  toggleUploadFrequencyDropdown(): void {
    if (!this.editableMode) return;
    this.isUploadFrequencyDropdownOpen = !this.isUploadFrequencyDropdownOpen;
    if (this.isUploadFrequencyDropdownOpen) {
      this.isResourceServerDropdownOpen = false;
      this.isLicenseDropdownOpen = false;
      this.isDatasetTypeDropdownOpen = false;
      this.isFileFormatDropdownOpen = false;
    }
  }

  selectUploadFrequency(option: { value: string; label: string }): void {
    this.metadataForm.patchValue({ uploadFrequency: option.value });
    this.metadataForm.get('uploadFrequency')?.markAsTouched();
    this.metadataForm.markAsDirty();
    this.isUploadFrequencyDropdownOpen = false;
  }

  getSelectedUploadFrequencyLabel(): string {
    const v = this.metadataForm.get('uploadFrequency')?.value;
    return this.getUploadFrequencyLabel(v) || 'Select upload frequency';
  }

  isUploadFrequencySelected(value: string): boolean {
    return this.metadataForm.get('uploadFrequency')?.value === value;
  }

  // --- Editable mode dropdowns: License ---
  toggleLicenseDropdown(): void {
    if (!this.editableMode) return;
    this.isLicenseDropdownOpen = !this.isLicenseDropdownOpen;
    if (this.isLicenseDropdownOpen) {
      this.isResourceServerDropdownOpen = false;
      this.isUploadFrequencyDropdownOpen = false;
      this.isDatasetTypeDropdownOpen = false;
      this.isFileFormatDropdownOpen = false;
    }
  }

  selectLicense(option: { value: string; label: string }): void {
    this.metadataForm.patchValue({ license: option.value });
    this.metadataForm.get('license')?.markAsTouched();
    this.metadataForm.markAsDirty();
    this.isLicenseDropdownOpen = false;
  }

  getSelectedLicenseLabel(): string {
    const v = this.metadataForm.get('license')?.value;
    if (!v) return 'Select license';
    return v === 'Other' ? 'Other' : (this.getLicenseLabel(v) || 'Select license');
  }

  isLicenseSelected(value: string): boolean {
    return this.metadataForm.get('license')?.value === value;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target?.closest?.('.custom-dropdown-wrapper')) return;
    this.isResourceServerDropdownOpen = false;
    this.isUploadFrequencyDropdownOpen = false;
    this.isLicenseDropdownOpen = false;
    this.isDatasetTypeDropdownOpen = false;
    this.isFileFormatDropdownOpen = false;
  }

  // --- Editable mode dropdowns: Dataset Type ---
  toggleDatasetTypeDropdown(): void {
    if (!this.editableMode) return;
    this.isDatasetTypeDropdownOpen = !this.isDatasetTypeDropdownOpen;
    if (this.isDatasetTypeDropdownOpen) {
      this.isFileFormatDropdownOpen = false;
    }
  }

  selectDatasetType(option: { value: string; label: string }): void {
    this.additionalInfoForm.patchValue({ datasetType: option.value });
    this.additionalInfoForm.get('datasetType')?.markAsTouched();
    this.additionalInfoForm.markAsDirty();
    this.updateFileFormatOptions(option.value);
    this.isDatasetTypeDropdownOpen = false;
  }

  getSelectedDatasetTypeLabel(): string {
    const v = this.additionalInfoForm.get('datasetType')?.value;
    return this.getDatasetTypeLabel(v) || 'Select dataset type';
  }

  isDatasetTypeSelected(value: string): boolean {
    return this.additionalInfoForm.get('datasetType')?.value === value;
  }

  // --- Editable mode dropdowns: File Format ---
  toggleFileFormatDropdown(): void {
    if (!this.editableMode) return;
    this.isFileFormatDropdownOpen = !this.isFileFormatDropdownOpen;
    if (this.isFileFormatDropdownOpen) {
      this.isDatasetTypeDropdownOpen = false;
    }
  }

  selectFileFormat(option: { value: string; label: string }): void {
    this.additionalInfoForm.patchValue({ fileFormat: option.value });
    this.additionalInfoForm.get('fileFormat')?.markAsTouched();
    this.additionalInfoForm.markAsDirty();
    this.isFileFormatDropdownOpen = false;
  }

  getSelectedFileFormatLabel(): string {
    const v = this.additionalInfoForm.get('fileFormat')?.value;
    return this.getFileFormatLabel(v) || 'Select file format';
  }

  isFileFormatSelected(value: string): boolean {
    return this.additionalInfoForm.get('fileFormat')?.value === value;
  }

  /**
   * Get tags array
   */
  getTags(): string[] {
    return this.additionalInfoForm.get('tags')?.value || [];
  }

  /** Highlight existing tag chip when input text matches it (same as usecase add tags). */
  isTagDuplicate(tag: string): boolean {
    const input = (this.tagInput || '').trim();
    if (!input) return false;
    return tag.toLowerCase() === input.toLowerCase();
  }

  /**
   * Get data descriptors (normalized to { attributeName, parameterType, schemaType } for template).
   * Uses getRawValue() so disabled controls (e.g. in preview mode) are included.
   */
  getDataDescriptors(): any[] {
    if (!this.additionalInfoForm) return [];
    const raw = this.additionalInfoForm.getRawValue?.();
    const descriptors = raw?.dataDescriptors ?? this.additionalInfoForm.get('dataDescriptors')?.value;
    if (!Array.isArray(descriptors)) return [];
    const out: any[] = [];
    descriptors.forEach((desc: any) => {
      const normalized = this.normalizeDataDescriptor(desc);
      if (normalized) out.push(normalized);
    });
    return out;
  }

  /**
   * Check if data descriptors exist and are valid
   */
  hasDataDescriptors(): boolean {
    // Use cached list for better performance and reliability
    return Array.isArray(this.dataDescriptorsList) && this.dataDescriptorsList.length > 0;
  }

  /**
   * Check if other license is selected
   */
  isOtherLicenseSelected(): boolean {
    return this.metadataForm.get('license')?.value === 'Other';
  }

  /**
   * Format year range for display
   */
  formatYearRange(yearRange: any): string {
    if (!yearRange) return '';
    if (typeof yearRange === 'object' && yearRange.startYear && yearRange.endYear) {
      return `${yearRange.startYear} - ${yearRange.endYear}`;
    }
    return String(yearRange);
  }

  /**
   * Check if data source field has a value
   */
  hasDataSource(): boolean {
    const value = this.additionalInfoForm.get('sampleDataUrl')?.value;
    return value && value.trim().length > 0;
  }

  ngAfterViewInit(): void {
    // Update step 3 with template reference after view is initialized.
    // static: false is required because #step3Template is inside *ngIf="!editableMode".
    if (this.step3Template && this.stepperSteps?.length > 2) {
      this.stepperSteps[2] = { ...this.stepperSteps[2], content: this.step3Template };
      this.cdr.detectChanges();
    }
  }

  onStepChange(stepIndex: number): void {
    const currentUrl = this.router.url;
    let routeBase: string;

    if (currentUrl.includes('/add-dataset/')) {
      routeBase = 'provider/add-contributions/add-dataset';
    } else if (currentUrl.includes('/add-model/')) {
      routeBase = 'provider/add-contributions/add-model';
    } else {
      // Default to add-dataset if we can't determine from URL
      routeBase = 'provider/add-contributions/add-dataset';
    }

    const routes = [
      routeBase,
      `${routeBase}/upload-metadata`,
      `${routeBase}/preview-metadata`,
      `${routeBase}/uploads`
    ];

    if (stepIndex >= 0 && stepIndex < routes.length) {
      this.router.navigate([routes[stepIndex]]);
    }
  }

  async onNext(): Promise<void> {
    // Check if already creating to prevent duplicate calls
    if (this.isCreatingItem) {
      console.warn('Already creating item, skipping duplicate call');
      return;
    }

    try {
      this.isCreatingItem = true;
      console.log('Creating item from Preview Metadata step...');

      // Get form data from localStorage
      const localStorageKey = this.getLocalStorageKey();
      const existingData = localStorage.getItem(localStorageKey);
      const formDataFromStorage = existingData ? JSON.parse(existingData) : {};

      // Merge current form values (what's displayed in Preview) so payload matches the form
      const metadataValues = this.metadataForm?.getRawValue?.() || {};
      const additionalValues = this.additionalInfoForm?.getRawValue?.() || {};
      const formData = {
        ...formDataFromStorage,
        ...metadataValues,
        ...additionalValues,
        title: this.datasetData?.title ?? formDataFromStorage.title,
        permission: this.datasetData?.permission ?? formDataFromStorage.permission
      };

      // Check if item already exists
      let itemId: string | null = formData.itemId || formData.id || formData.item_id || null;

      // If item doesn't exist, create it
      if (!itemId) {
        console.log('No itemId found; creating item...');
        const itemPayload = await this.buildCreateAssetPayload(formData);
        const response = await this.catalogueApiService.createAssetItem(itemPayload);

        // Extract item ID from response
        const createdItemId =
          response?.result?.itemId ||
          response?.result?.id ||
          response?.data?.id ||
          response?.id ||
          response?.result?.data?.id;

        if (createdItemId) {
          itemId = createdItemId;
          console.log('Item created successfully with ID:', createdItemId);

          // Store item ID in localStorage
          formData.itemId = createdItemId;
          formData.id = createdItemId;
          localStorage.setItem(localStorageKey, JSON.stringify(formData));
        } else {
          console.warn('Item ID not found in create response:', response);
          this.toasterService.error('Failed to create dataset. Please try again.');
          return;
        }
      } else {
        console.log('Item already exists with ID:', itemId);
      }

      // Navigate to next step (uploads)
      this.proceed();
    } catch (error) {
      console.error('Error creating item:', error);
      this.toasterService.error('Failed to create dataset. Please try again.');
    } finally {
      this.isCreatingItem = false;
    }
  }

  onPrevious(): void {
    // Navigate to previous step (upload-metadata)
    this.goBack();
  }

  goBack(): void {
    this.router.navigate(['../upload-metadata'], { relativeTo: this.route });
  }

  proceed(): void {
    // Navigate to uploads (Step 4: Upload Data)
    this.router.navigate(['../uploads'], { relativeTo: this.route });
  }

  /**
   * Build payload for createAssetItem API
   */
  private async buildCreateAssetPayload(formData: any): Promise<any> {
    // Check if this is a model or dataset
    const isModel = this.router.url.includes('/add-model/');

    // Get user and organization info
    let userData: any = null;
    let organizationsList: any[] = [];

    try {
      userData = await this.authApiService.getUserInfo();
      // Get all organizations to find the matching one
      const orgsResponse = await this.authApiService.getAllOrganisations();
      organizationsList = orgsResponse?.result || orgsResponse?.data || [];
    } catch (error) {
      console.error('Error fetching user/organization info:', error);
    }

    // Extract organization info from userData
    const result = userData?.result || userData || {};
    const organisation = result.organisation || {};
    const userOrganisationId = organisation.organisationId || organisation.id || '';

    // Find matching organization from the list
    let organisationName = '';
    let organisationType = 'Public';
    let industry = 'forest';
    let department = '';

    if (userOrganisationId && organizationsList.length > 0) {
      const matchedOrg = organizationsList.find(
        (org: any) => (org.id === userOrganisationId || org.organisationId === userOrganisationId)
      );
      if (matchedOrg) {
        organisationName = matchedOrg.orgName || matchedOrg.name || '';
        organisationType = matchedOrg.organisationType || matchedOrg.type || 'Public';
        industry = matchedOrg.industry || 'forest';
        department = matchedOrg.department || matchedOrg.dept || matchedOrg.departmentName || '';
      }
    }

    // Get user email for uploadedBy (prefer email over userName)
    const uploadedBy = result.email || result.emailAddress || result.registeredEmail || result.userName || result.name || '';

    // Build payload based on type (model or dataset)
    if (isModel) {
      // Build model payload
      const accessPolicy = formData.permission === 'Restricted' ? 'PRIVATE' : 'OPEN';

      // Ensure tags is an array
      let tagsArray: string[] = [];
      if (Array.isArray(formData.tags)) {
        tagsArray = formData.tags;
      } else if (formData.tags) {
        tagsArray = typeof formData.tags === 'string'
          ? formData.tags.split(',').map((t: string) => t.trim()).filter((t: string) => t.length > 0)
          : [formData.tags];
      }

      // Extract descriptions from formData or jsonSchema
      let shortDescription = formData.shortDescription || '';
      let longDescription = formData.longDescription || formData.description || '';

      // If descriptions are not in formData, check jsonSchema (for upload-metadata flow)
      if (!shortDescription || !longDescription) {
        try {
          const jsonSchema = formData.jsonSchema;
          if (jsonSchema) {
            const parsedSchema = typeof jsonSchema === 'string' ? JSON.parse(jsonSchema) : jsonSchema;
            shortDescription = shortDescription || parsedSchema.shortDescription || '';
            longDescription = longDescription || parsedSchema.longDescription || parsedSchema.description || '';
          }
        } catch (error) {
          console.warn('Error parsing jsonSchema for descriptions:', error);
        }
      }

      const payload: any = {
        type: ['adex:AiModel'],
        name: formData.title || '',
        description: longDescription,
        shortDescription: shortDescription,
        label: formData.title || '',
        tags: tagsArray,
        accessPolicy: accessPolicy,
        organizationId: userOrganisationId || '',
        organizationType: organisationType || 'Public',
        department: department || '',
        modelType: formData.datasetType || '',
        fileFormat: (formData.fileFormat || '').toLowerCase(),
        industry: industry || 'Agriculture',
        uploadedBy: uploadedBy,
        license: formData.license || 'MIT',
        fileSize: '15MB',
        mediaURL: formData.sampleDataUrl || 'https://iudx.io/'
      };

      console.log('Model payload built:', JSON.stringify(payload, null, 2));
      return payload;
    } else {
      // Build dataset payload
      const resourceServerValue = formData.resourceServer || '';
      const resourceServerArray = this.buildResourceServerArray(resourceServerValue);

      // Build dataDescriptor from form data
      const dataDescriptor: any = {};
      const descriptors = formData.dataDescriptors || [];
      if (descriptors && descriptors.length > 0) {
        descriptors.forEach((desc: any) => {
          if (desc.attributeName) {
            dataDescriptor[desc.attributeName] = {
              parameterType: desc.parameterType || '',
              schemaType: desc.schemaType || ''
            };
          }
        });
      }

      const payload: any = {
        '@context': 'https://voc.forest-stack.iudx.io/',
        type: ['adex:DataBank'],
        name: formData.title || '',
        shortDescription: formData.shortDescription || '',
        description: formData.longDescription || formData.description || '',
        tags: formData.tags || [],
        concepts: formData.concepts || ['', 'Administration'],
        accessPolicy: (formData.permission || 'OPEN').toUpperCase(),
        apdURL: 'acl-apd.forestdx.iudx.io',
        organization: organisationName || 'Rajasthan Forestry',
        organizationId: userOrganisationId || '6e5bd047-9eb8-4bba-b72e-f57ce3e23ce6',
        organizationType: organisationType || 'Public',
        industry: industry || 'forest',
        uploadedBy: uploadedBy || 'Rajasthan Forest Department Data Provider',
        verifiedBy: '-',
        ownerUserId: result.userId || '',
        license: formData.license || 'Open Government License, India',
        yearRange: this.formatYearRange(formData.yearRange) || formData.yearRange || '2020 - 2021',
        geoCoverage: formData.geoCoverage || 'Test',
        uploadFrequency: formData.uploadFrequency || 'One-time',
        dataBankType: formData.datasetType || 'Structured',
        fileFormat: (formData.fileFormat || 'csv').toLowerCase(),
        dataSample: formData.dataSample || {
          info: {
            _postman_id: '52ddcd25-f6d8-423d-922e-42bbc3dae156',
            name: 'New Collection',
            schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json',
            _exporter_id: '45165347',
            _collection_link: 'https://pradeep-2774864.postman.co/workspace/My-Workspace~2ee2a408-fb23-433d-8073-9d9a27b84bbc/collection/45165347-52ddcd25-f6d8-423d-922e-42bbc3dae156?action=share&source=collection_link&creator=45165347'
          },
          item: []
        },
        resourceServer: resourceServerArray,
        dataDescriptor: dataDescriptor
      };

      // If Data Source (sampleDataUrl) is provided, add mediaURL to payload (required)
      if (formData.sampleDataUrl && formData.sampleDataUrl.trim().length > 0) {
        payload.mediaURL = formData.sampleDataUrl.trim();
      }

      console.log('Dataset payload built:', JSON.stringify(payload, null, 2));
      return payload;
    }
  }

  /**
   * Build resource server array from form data
   */
  private buildResourceServerArray(resourceServerValue: string | any): any[] {
    const resourceServerArray: any[] = [];

    if (!resourceServerValue) {
      // Default resource servers if none selected
      resourceServerArray.push({
        id: 'fb54d575-a8e2-47d1-ab34-1a89cb907ff1',
        name: 'FILE',
        queryTypes: ['ATTR'],
        accessTypes: ['ATTR'],
        type: 'file',
        url: ''
      });
      return resourceServerArray;
    }

    // If resourceServerValue is a string (URL), try to determine type
    if (typeof resourceServerValue === 'string') {
      let name = 'FILE';
      let type = 'file';
      let url = resourceServerValue;

      if (resourceServerValue.includes('ogc')) {
        name = 'OGC';
        type = 'ogc';
      } else if (resourceServerValue.includes('rs') || resourceServerValue.includes('ngsi')) {
        name = 'NGSI-LD';
        type = 'ngsi-ld';
      }

      resourceServerArray.push({
        id: 'fb54d575-a8e2-47d1-ab34-1a89cb907ff1',
        name: name,
        queryTypes: ['ATTR'],
        accessTypes: ['ATTR'],
        type: type,
        url: url
      });
    } else if (typeof resourceServerValue === 'object') {
      resourceServerArray.push({
        id: resourceServerValue.id || 'fb54d575-a8e2-47d1-ab34-1a89cb907ff1',
        name: resourceServerValue.name || 'FILE',
        queryTypes: resourceServerValue.queryTypes || ['ATTR'],
        accessTypes: resourceServerValue.accessTypes || ['ATTR'],
        type: resourceServerValue.type || 'file',
        url: resourceServerValue.url || ''
      });
    } else if (Array.isArray(resourceServerValue)) {
      resourceServerArray.push(...resourceServerValue.map((rs: any) => ({
        id: rs.id || 'fb54d575-a8e2-47d1-ab34-1a89cb907ff1',
        name: rs.name || 'FILE',
        queryTypes: rs.queryTypes || ['ATTR'],
        accessTypes: rs.accessTypes || ['ATTR'],
        type: rs.type || 'file',
        url: rs.url || ''
      })));
    }

    return resourceServerArray;
  }

  /** UI only: show "Public" for "Open", API still uses OPEN */
  getPermissionDisplayLabel(permission: string | null | undefined): string {
    return !permission ? '' : permission === 'Open' ? 'Public' : permission;
  }
}
